import { css, ThemeContext } from '@emotion/react';
import styled from '@emotion/styled';
import { useRef, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { GameDifficulty, OnlineOpponent, OnlineStatus } from 'graphql/types';

import colors from 'lib/colors';
import { AppTheme } from 'theme';

import { Box, Button } from 'components';

import StatusBadge from './StatusBadge';

interface Props {
  onlineUsers: OnlineOpponent[] | undefined;
  onSelected: (user: OnlineOpponent) => void;
  gameDifficulty: GameDifficulty;
  onBottomReached: (currentSize: number) => void;
  searchActive: boolean;
}

const OnlineUsersList = ({
  onlineUsers,
  onSelected,
  gameDifficulty,
  onBottomReached,
  searchActive,
}: Props) => {
  const [t] = useTranslation();

  const theme = useContext(ThemeContext) as AppTheme;
  const IS_EDU = theme?.layout?.edu;

  const listRef = useRef<HTMLUListElement>(null);

  const onScroll = () => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollTop + clientHeight === scrollHeight) {
        onBottomReached(listRef.current.childElementCount);
      }
    }
  };

  return (
    <>
      {onlineUsers == null || !onlineUsers.length ? (
        <EmptyView>
          {searchActive && (
            <Trans t={t} i18nKey="lobby.foundUsers.empty">
              Nikdo nenalezen.
              <br />
              Zkuste se zeptat kolegy, jestli se nechce připojit.
            </Trans>
          )}
          {!searchActive && (
            <Trans t={t} i18nKey="lobby.onlineUsers.empty">
              Nikdo není online.
              <br />
              Zkuste se zeptat kolegy, jestli se nechce připojit.
            </Trans>
          )}
        </EmptyView>
      ) : (
        <List onScroll={() => onScroll()} ref={listRef}>
          {onlineUsers.map((opponent) => {
            const user = opponent.user;
            const userIsOnline = opponent.status === OnlineStatus.ONLINE;
            const userHasMediumDifficulty =
              user.gameDifficulty === GameDifficulty.MEDIUM;
            const iHaveMediumDifficulty =
              gameDifficulty === GameDifficulty.MEDIUM;
            return (
              <ListItem key={user.id}>
                <Box display="inline-block">{user.name || user.email}</Box>
                <StatusBadge status={opponent.status} />
                {iHaveMediumDifficulty &&
                userHasMediumDifficulty &&
                userIsOnline ? (
                  <Button
                    labelT={{
                      key: 'lobby.onlineUsers.invite',
                      defaultValue: 'Pozvat do hry',
                    }}
                    variant={IS_EDU ? 'primary' : 'warning'}
                    css={{
                      ...smallButton,
                      borderWidth: IS_EDU ? undefined : 1,
                    }}
                    onClick={() => {
                      onSelected(opponent);
                    }}
                  />
                ) : iHaveMediumDifficulty && userHasMediumDifficulty ? (
                  <Button
                    labelT={{
                      key: 'lobby.onlineUsers.inviteAndWait',
                      defaultValue: 'Pozvat a počkat',
                    }}
                    variant={IS_EDU ? 'outline-primary' : 'transparent'}
                    css={{
                      ...smallButton,
                      borderWidth: IS_EDU ? undefined : 1,
                    }}
                    onClick={() => {
                      onSelected(opponent);
                    }}
                  />
                ) : (
                  <Button
                    labelT={{
                      key: 'lobby.onlineUsers.invite',
                      defaultValue: 'Pozvat do hry',
                    }}
                    disabled
                    tooltip={
                      !iHaveMediumDifficulty
                        ? t(
                            'lobby.game.notAvailableOnEasyGame',
                            'Dostupné pouze v pokročilé hře',
                          )
                        : t(
                            'lobby.game.inviteIntoMediumGameForPlay',
                            'Pozvěte kolegu do pokročilé hry, kde si proti sobě můžete zahrát',
                          )
                    }
                    css={smallButtonDisabled}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      )}
    </>
  );
};

export default OnlineUsersList;

const smallButton = {
  margin: '0 0 0 10px',
  padding: '0.3em 0',
  width: '25ch',
};

const smallButtonDisabled = css`
  margin: 0 0 0 10px;
  padding: 0.3em 0;
  width: 25ch;
  background: ${colors.gray};
  color: ${colors.disabledInputText};
  border: 1px solid ${colors.gray};
  float: right;
  div {
    padding-right: 0;
    img {
      height: 12px;
      margin-top: 1px;
      margin-left: 5px;
    }
  }
`;

const EmptyView = styled.p`
  flex: 1;
  margin: 0;
  padding: 0;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #9593ae;
  text-align: center;
`;

const List = styled.ul`
  flex: 0 0 auto;
  padding: 0;
  margin: 4px 0;
  height: 100%;
  overflow-y: auto;
  list-style: none;
  &::-webkit-scrollbar {
    width: 7px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #d8d4fd;
    border-radius: 3px;
    border: 1px solid #d8d4fd;
  }
  &::-webkit-scrollbar-track {
    background: none;
  }
`;

const ListItem = styled.li`
  display: flex;
  justify-content: right;
  margin: 0;
  padding: 4px 4px;
  color: white;
  font-size: 0.875rem;
  line-height: 1.5714285714;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
`;
